// apps/client/src/components/help/HelpTooltip.tsx
// Reusable hover/tap tooltip with a HelpCircle trigger, rem-anchored sizing.
// Used inline next to metric labels (stat cards, table headers, etc.).
//
// Mobile: tap toggles visibility (since hover doesn't exist).
// Desktop: hover shows; click pins open until clicked-away.

import React, { useEffect, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';

export interface HelpTooltipContent {
  definition: string;
  formula?: string;
}

interface HelpTooltipProps {
  content: HelpTooltipContent;
  /** Called the first time the tooltip is opened. Fire telemetry from here. */
  onOpen?: () => void;
  /** Optional aria-label override; defaults to "Help" */
  ariaLabel?: string;
  /** Size of the trigger icon in lucide units. Defaults to 14. */
  iconSize?: number;
  /** Popover above or below the trigger. Defaults to below. Use 'top' near the bottom of a scroll container. */
  placement?: 'top' | 'bottom';
  /** Popover edge anchored to the trigger. Defaults to 'left'. Use 'right' near the right edge of a container. */
  align?: 'left' | 'right';
}

/**
 * Renders a small HelpCircle icon. Hover/focus/tap reveals a dark popover
 * showing the definition and optional formula.
 *
 * Width is `max-w-xs` (20rem) — readable on a 14" MacBook (1512px) without
 * overflowing the parent card; on mobile collapses to viewport width.
 */
export const HelpTooltip: React.FC<HelpTooltipProps> = ({
  content,
  onOpen,
  ariaLabel = 'Help',
  iconSize = 14,
  placement = 'bottom',
  align = 'left',
}) => {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const firedOpenRef = useRef(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  const fireOpenOnce = () => {
    if (!firedOpenRef.current) {
      firedOpenRef.current = true;
      onOpen?.();
    }
  };

  // Click-outside to unpin
  useEffect(() => {
    if (!pinned) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setPinned(false);
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pinned]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (pinned) {
      setPinned(false);
      setOpen(false);
    } else {
      setPinned(true);
      setOpen(true);
      fireOpenOnce();
    }
  };

  const handleMouseEnter = () => {
    if (pinned) return;
    setOpen(true);
    fireOpenOnce();
  };

  const handleMouseLeave = () => {
    if (pinned) return;
    setOpen(false);
  };

  const handleFocus = () => {
    setOpen(true);
    fireOpenOnce();
  };

  const handleBlur = () => {
    if (pinned) return;
    setOpen(false);
  };

  return (
    <span
      ref={wrapperRef}
      className="relative inline-flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="inline-flex items-center justify-center text-gray-500 hover:text-blue-400 focus:text-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500/50 rounded transition-colors"
      >
        <HelpCircle size={iconSize} aria-hidden="true" />
      </button>

      {open && (
        <span
          role="tooltip"
          className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} ${placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} z-20 w-screen max-w-xs sm:w-80 rounded-lg border border-gray-700 bg-gray-900 shadow-2xl p-3 text-left animate-in fade-in duration-150`}
        >
          <span className="block text-sm text-gray-200 leading-relaxed">
            {content.definition}
          </span>
          {content.formula && (
            <span className="block mt-2 pt-2 border-t border-gray-700/60 text-xs text-gray-400 leading-relaxed">
              <span className="font-medium text-gray-300">Formula: </span>
              {content.formula}
            </span>
          )}
        </span>
      )}
    </span>
  );
};

export default HelpTooltip;
